import { useEffect, useState } from 'react';
import { History, Pencil, Plus, Search, X } from 'lucide-react';
import { customerRepository, orderRepository } from '../../db/repositories';
import {
  createCustomer,
  updateCustomer,
  type CustomerInput,
} from '../../domain/customers/customerService';
import { maskMobile } from '../../domain/customers/customerUtils';
import type { CustomerRecord, OrderRecord } from '../../db/types';
import { PricingEngine } from '../../domain/pricing';

type Props = { onClose: () => void };
const empty: CustomerInput = { name: '', mobileNumber: '', address: '', notes: '' };

export function CustomerManagementDialog({ onClose }: Props) {
  const [query, setQuery] = useState('');
  const [customers, setCustomers] = useState<CustomerRecord[]>([]);
  const [selected, setSelected] = useState<CustomerRecord | null>(null);
  const [editing, setEditing] = useState<CustomerInput | null>(null);
  const [history, setHistory] = useState<OrderRecord[]>([]);
  const [error, setError] = useState('');

  const load = async () => setCustomers(await customerRepository.search(query));
  useEffect(() => {
    void load();
  }, [query]);

  const selectCustomer = async (customer: CustomerRecord) => {
    setSelected(customer);
    setEditing({
      name: customer.name,
      mobileNumber: customer.mobileNumber ?? '',
      address: customer.address ?? '',
      notes: customer.notes ?? '',
    });
    const orders = (await orderRepository.list()).filter(
      (order) => order.customerId === customer.id,
    );
    setHistory(orders);
  };

  const save = async () => {
    if (!editing) return;
    try {
      setError('');
      const customer = selected
        ? await updateCustomer(selected.id, editing)
        : await createCustomer(editing);
      await load();
      await selectCustomer(customer);
      if (!selected) setEditing(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to save customer.');
    }
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-950/40 p-5">
      <div className="flex max-h-[92dvh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-[#00549C]">
              Customer management
            </p>
            <h2 className="text-2xl font-black text-[#003B70]">Customers</h2>
          </div>
          <button
            onClick={onClose}
            className="grid h-11 w-11 place-items-center rounded-xl bg-slate-100"
            aria-label="Close"
          >
            <X />
          </button>
        </header>
        <div className="grid min-h-0 flex-1 gap-4 overflow-auto p-5 lg:grid-cols-[1fr_1.15fr]">
          <section className="min-h-0 rounded-2xl border border-slate-200 bg-[#F7FAFC] p-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={18}
                />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Name, customer code, or mobile"
                  className="h-12 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 font-semibold outline-none focus:border-[#00549C]"
                />
              </div>
              <button
                onClick={() => {
                  setSelected(null);
                  setEditing(empty);
                  setHistory([]);
                  setError('');
                }}
                className="grid h-12 w-12 place-items-center rounded-xl bg-[#00549C] text-white"
                aria-label="New customer"
              >
                <Plus />
              </button>
            </div>
            <div className="mt-3 space-y-2 overflow-auto lg:max-h-[58dvh]">
              {customers.map((customer) => (
                <button
                  key={customer.id}
                  onClick={() => void selectCustomer(customer)}
                  className={`w-full rounded-xl border p-3 text-left ${selected?.id === customer.id ? 'border-[#00549C] bg-[#DCEEFF]' : 'border-slate-200 bg-white'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-black text-slate-800">{customer.name}</p>
                      <p className="text-xs font-bold text-slate-400">
                        {customer.customerCode ?? 'Legacy customer'}{' '}
                        {customer.mobileNumber ? `• ${maskMobile(customer.mobileNumber)}` : ''}
                      </p>
                    </div>
                    <Pencil size={16} className="text-slate-400" />
                  </div>
                </button>
              ))}
              {customers.length === 0 && (
                <p className="p-8 text-center text-sm font-semibold text-slate-400">
                  No customers found.
                </p>
              )}
            </div>
          </section>
          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            {editing ? (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-extrabold uppercase tracking-widest text-[#00549C]">
                      {selected ? 'Edit customer' : 'New customer'}
                    </p>
                    <h3 className="text-xl font-black text-[#003B70]">Customer details</h3>
                  </div>
                  {selected && (
                    <span className="rounded-lg bg-[#DCEEFF] px-3 py-2 text-xs font-black text-[#00549C]">
                      {selected.customerCode}
                    </span>
                  )}
                </div>
                <div className="mt-4 grid gap-3">
                  <label className="text-sm font-bold text-slate-700">
                    Full name
                    <input
                      type="text"
                      value={editing.name}
                      onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      className="mt-1 h-14 w-full rounded-xl border border-slate-200 px-4 text-lg font-semibold outline-none focus:border-[#00549C]"
                    />
                  </label>
                  <label className="text-sm font-bold text-slate-700">
                    Mobile number
                    <input
                      type="tel"
                      inputMode="tel"
                      value={editing.mobileNumber}
                      onChange={(e) => setEditing({ ...editing, mobileNumber: e.target.value })}
                      className="mt-1 h-14 w-full rounded-xl border border-slate-200 px-4 text-lg font-semibold outline-none focus:border-[#00549C]"
                    />
                  </label>
                  <label className="text-sm font-bold text-slate-700">
                    Address
                    <input
                      type="text"
                      value={editing.address}
                      onChange={(e) => setEditing({ ...editing, address: e.target.value })}
                      className="mt-1 h-14 w-full rounded-xl border border-slate-200 px-4 font-semibold outline-none focus:border-[#00549C]"
                    />
                  </label>
                  <label className="text-sm font-bold text-slate-700">
                    Notes
                    <textarea
                      value={editing.notes}
                      onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                      className="mt-1 min-h-24 w-full rounded-xl border border-slate-200 p-4 font-medium outline-none focus:border-[#00549C]"
                    />
                  </label>
                  {error && (
                    <p className="rounded-xl bg-red-50 p-3 text-sm font-bold text-red-600">
                      {error}
                    </p>
                  )}
                  <button
                    onClick={() => void save()}
                    disabled={!editing.name.trim()}
                    className="min-h-[60px] rounded-xl bg-[#00549C] text-lg font-black text-white disabled:opacity-40"
                  >
                    {selected ? 'SAVE CUSTOMER CHANGES' : 'CREATE CUSTOMER'}
                  </button>
                </div>
                {selected && (
                  <div className="mt-6 border-t border-slate-100 pt-5">
                    <div className="flex items-center gap-2">
                      <History size={18} className="text-[#00549C]" />
                      <h4 className="font-black text-[#003B70]">Customer history</h4>
                    </div>
                    <div className="mt-3 space-y-2">
                      {history.map((order) => (
                        <div
                          key={order.id}
                          className="flex items-center justify-between rounded-xl bg-[#F7FAFC] p-3"
                        >
                          <div>
                            <p className="font-black text-slate-700">{order.orderNumber}</p>
                            <p className="text-xs font-semibold text-slate-400">
                              {order.orderStatus} •{' '}
                              {new Date(order.createdAt).toLocaleDateString('en-PH')}
                            </p>
                          </div>
                          <span className="font-black text-[#003B70]">
                            {PricingEngine.formatCentavos(order.totalCentavos)}
                          </span>
                        </div>
                      ))}
                      {history.length === 0 && (
                        <p className="text-sm font-semibold text-slate-400">No order history.</p>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="grid h-full min-h-[400px] place-items-center text-center text-slate-400">
                <div>
                  <History size={42} className="mx-auto opacity-40" />
                  <p className="mt-3 font-black">Select a customer</p>
                  <p className="text-sm">Create, edit, and review permitted customer history.</p>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
